import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Calculator, Truck, MessageSquare, BookOpen, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useFoundingStatus } from "@/hooks/useFoundingStatus";
import { FoundingBadge } from "@/components/FoundingBadge";
import FoundingBanner from "@/components/FoundingBanner";
import { COMMUNITY_FEATURES, PRO_FEATURES, PRO_PLANS, ANNUAL_SAVINGS } from "@/data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const founding = useFoundingStatus();
  const plan = PRO_PLANS[billing];
  const proPrice = plan.price;
  const proSub = plan.sub;

  const doCancel = async () => {
    setCanceling(true);
    try {
      await axios.post(`${API}/subscription/cancel`);
      await refresh();
      setShowCancel(false);
      toast.success("Your plan is canceled — you keep access until it ends.");
    } catch {
      toast.error("Could not cancel right now. Please try again.");
    } finally {
      setCanceling(false);
    }
  };

  const doResume = async () => {
    try {
      await axios.post(`${API}/subscription/resume`);
      await refresh();
      toast.success("Your plan will continue.");
    } catch {
      toast.error("Could not update. Please try again.");
    }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    let stopped = false;
    const finish = (ok, msg) => {
      if (stopped) return;
      stopped = true;
      setVerifying(false);
      setParams({}, { replace: true });
      if (msg) (ok ? toast.success : toast.error)(msg);
    };
    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const token = localStorage.getItem("rh_token");
        const { data } = await axios.get(`${API}/checkout/status/${sid}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        if (data.payment_status === "paid" || data.status === "complete") {
          await refresh();
          finish(true, "You're subscribed! Welcome to the paid tier.");
          navigate("/loadboard", { replace: true });
          return;
        }
        if (data.status === "expired") {
          finish(false, "Payment wasn't completed. You can try again anytime.");
          return;
        }
      } catch {
        // transient — keep polling until attempts are exhausted
      }
      if (attempts >= 8) {
        finish(false, "We couldn't confirm your payment yet. If you completed it, refresh in a moment.");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, []); // eslint-disable-line

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading("");
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="payment-confirming">
          <Loader2 className="w-10 h-10 text-brand animate-spin mx-auto" />
          <h1 className="mt-6 font-heading text-2xl font-black text-slate-900">Confirming your payment…</h1>
          <p className="mt-2 text-slate-500 text-sm">This only takes a few seconds. Please don't close this tab.</p>
          <Link to="/#playbook" data-testid="confirm-escape" className="mt-8 inline-block text-sm text-slate-400 hover:text-brand transition-colors">Taking too long? Go to your guide →</Link>
        </div>
      </div>
    );
  }

  if (user?.subscription_active) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">You're on the paid tier 🎉</h1>
          {user?.founding_member && <div className="mt-4 flex justify-center"><FoundingBadge /></div>}
          <p className="text-slate-600 mt-3">Your {user.plan} plan includes <span className="font-semibold text-slate-900">everything in the free tier</span> — the full 12-Step Business Foundation Guide and diagnostic — plus your premium tools.</p>
          <div className="flex gap-3 justify-center mt-8 flex-wrap">
            <Link to="/#playbook"><Button variant="outline" className="rounded-full border-slate-300 h-12 px-6 font-bold" data-testid="subscribe-playbook-link"><BookOpen className="w-4 h-4 mr-2" />12-Step Business Foundation Guide</Button></Link>
            <Link to="/loadboard"><Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6" data-testid="subscribe-loadboard-link"><Truck className="w-4 h-4 mr-2" />Load Board</Button></Link>
            <Link to="/cpm"><Button variant="outline" className="rounded-full border-slate-300 h-12 px-6 font-bold" data-testid="subscribe-cpm-link"><Calculator className="w-4 h-4 mr-2" />CPM Calculator</Button></Link>
            <Link to="/coach"><Button variant="outline" className="rounded-full border-slate-300 h-12 px-6 font-bold" data-testid="subscribe-coach-link"><MessageSquare className="w-4 h-4 mr-2" />AI Coach</Button></Link>
          </div>

          <div className="mt-12 max-w-md mx-auto border border-slate-200 rounded-md p-6 text-left" data-testid="manage-plan-card">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-brand" />
              <h3 className="font-heading font-bold text-slate-900">Manage your plan</h3>
            </div>
            {user.has_stripe_subscription ? (
              <>
                {user.subscription_expires && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Your <span className="font-semibold text-slate-900">{user.plan}</span> plan renews automatically on{" "}
                    <span className="font-semibold text-slate-900">{fmtDate(user.subscription_expires)}</span> — you'll be
                    billed each {user.plan === "annual" ? "year" : "month"} until you cancel.
                  </p>
                )}
                {user.subscription_canceled ? (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-amber-700" data-testid="plan-canceled-note">
                      Canceled — you keep full access until {fmtDate(user.subscription_expires)}, then your plan won't renew.
                    </p>
                    <Button onClick={doResume} variant="outline" className="mt-3 rounded-full border-slate-300 font-bold" data-testid="resume-plan-button">
                      Keep my plan
                    </Button>
                  </div>
                ) : (
                  <button onClick={() => setShowCancel(true)} data-testid="cancel-plan-button" className="mt-4 text-sm font-bold text-slate-500 hover:text-red-600 transition-colors">
                    Cancel auto-renewal
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-600 leading-relaxed" data-testid="plan-active-note">
                Your <span className="font-semibold text-slate-900">{user.plan || "paid"}</span> access is active
                {user.subscription_expires ? <> until <span className="font-semibold text-slate-900">{fmtDate(user.subscription_expires)}</span></> : ""}. There's nothing to manage here.
              </p>
            )}
          </div>
        </div>

        <Dialog open={showCancel} onOpenChange={setShowCancel}>
          <DialogContent data-testid="cancel-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-black tracking-tight">Cancel auto-renewal?</DialogTitle>
              <DialogDescription className="leading-relaxed">
                You'll keep full access to all paid tools until{" "}
                <span className="font-semibold text-slate-900">{fmtDate(user.subscription_expires)}</span>. After that
                your plan won't renew and you won't be billed again. You can turn renewal back on anytime before it ends.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" className="rounded-full font-bold" onClick={() => setShowCancel(false)} data-testid="cancel-dialog-keep">
                Keep my plan
              </Button>
              <Button onClick={doCancel} disabled={canceling} className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="cancel-dialog-confirm">
                {canceling ? "Canceling…" : "Yes, cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Choose your plan</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900">Start free. Upgrade when you're ready to run the numbers.</h1>
          <p className="mt-4 text-slate-600">Two simple tiers. Learn the business for free — add the tools when you're ready to put it to work.</p>
        </div>

        {/* Founding member urgency */}
        <FoundingBanner founding={founding} className="mt-8" />

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-2 mt-10">
          <button
            onClick={() => setBilling("monthly")}
            data-testid="billing-monthly"
            className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("annual")}
            data-testid="billing-annual"
            className={`h-10 px-5 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-2 ${billing === "annual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            Annual <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{ANNUAL_SAVINGS}</span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8 max-w-3xl mx-auto items-stretch">
          {/* Community (Free) */}
          <div className="rounded-2xl border border-slate-200 p-8 flex flex-col" data-testid="tier-community">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold text-slate-900">Community</h3>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 bg-slate-100 rounded-full px-3 py-1">Your plan</span>
            </div>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-heading text-5xl font-black tracking-tight text-slate-900">$0</span>
              <span className="text-slate-500 mb-1.5">/forever</span>
            </div>
            <p className="text-sm text-slate-500 mt-2">Everything you need to learn the business.</p>
            <ul className="space-y-3 mt-6 flex-1">
              {COMMUNITY_FEATURES.map((f) => (
                <li key={f} className="flex gap-3 text-slate-700 text-sm"><Check className="w-5 h-5 text-emerald-500 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/#playbook" data-testid="tier-community-cta">
              <Button variant="outline" className="w-full mt-6 h-12 rounded-full border-slate-300 font-bold"><BookOpen className="w-4 h-4 mr-2" /> Go to your guide</Button>
            </Link>
          </div>

          {/* Pro (Paid) */}
          <div className="rounded-2xl border-2 border-brand ring-1 ring-brand/20 p-8 flex flex-col relative bg-orange-50/30" data-testid="tier-pro">
            <span className="absolute -top-3 left-8 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-brand rounded-full px-3 py-1">Most popular</span>
            <h3 className="font-heading text-xl font-bold text-slate-900">Pro</h3>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-heading text-5xl font-black tracking-tight text-slate-900" data-testid="pro-price">{proPrice}</span>
              <span className="text-slate-500 mb-1.5">{plan.per}</span>
            </div>
            <p className="text-sm text-slate-500 mt-2" data-testid="pro-sub">{proSub}</p>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-6 mb-3">Everything in Community, plus:</div>
            <ul className="space-y-3 flex-1">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex gap-3 text-slate-700 text-sm"><Check className="w-5 h-5 text-emerald-500 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button
              onClick={() => subscribe(billing)}
              disabled={loading === billing}
              data-testid="tier-pro-cta"
              className="w-full mt-6 h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-bold"
            >
              {loading === billing ? "Redirecting…" : `Upgrade — ${plan.label}`}
            </Button>
            <p className="text-xs text-center text-slate-400 mt-3">Cancel anytime. Secure checkout via Stripe.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
